import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

const initialPermissions = {
    appViewIds: [],
    roleIds: [],
    isAdmin: false,
    processExternal: false,
    companyApproved: false,
    loaded: false,
    error: false,
};

/**
 * Client-side permissions and session-bootstrap flag (not server cache).
 * Shape matches the former Redux PermissionReducer fields used by canAccess.
 */
export const usePermissionStore = create((set) => ({
    ...initialPermissions,
    /** User id for which companies/profile/role/permissions were loaded; null when cleared. */
    sessionBootstrappedForUserId: null,

    setPermissions: (payload) => set({
        appViewIds: payload.appViewIds ?? [],
        roleIds: payload.roleIds ?? [],
        isAdmin: payload.isAdmin ?? false,
        processExternal: payload.processExternal ?? false,
        companyApproved: payload.companyApproved ?? false,
        loaded: true,
        error: false,
    }),

    setPermissionsError: () => set({
        ...initialPermissions,
        loaded: true,
        error: true,
        sessionBootstrappedForUserId: null,
    }),

    clearPermissions: () => set({
        ...initialPermissions,
        sessionBootstrappedForUserId: null,
    }),

    /** Reset permission load state without dropping session bootstrap (for retry). */
    beginPermissionsReload: () => set((state) => ({
        ...initialPermissions,
        sessionBootstrappedForUserId: state.sessionBootstrappedForUserId,
    })),

    setSessionBootstrappedForUserId: (userId) => set({
        sessionBootstrappedForUserId: userId,
    }),
}));

/** Permission fields only — for canAccess / nav helpers. */
export function selectPermissionState(state) {
    return {
        appViewIds: state.appViewIds,
        roleIds: state.roleIds,
        isAdmin: state.isAdmin,
        processExternal: state.processExternal,
        companyApproved: state.companyApproved,
        loaded: state.loaded,
        error: state.error,
    };
}

/** Hook: same shape as former Redux PermissionReducer for canAccess callers. */
export function usePermissionState() {
    return usePermissionStore(useShallow(selectPermissionState));
}
